import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Button,
  IconButton,
  InputAdornment,
  Typography,
  Divider,
  Menu,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  MoreVert as MoreIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { contactApi } from '../services/api';
import { useApp } from '../context/AppContext';
import { LoadingSpinner, EmptyState } from '../components/common';
import { formatRelativeDate, debounce } from '../utils/helpers';

const statusColors = {
  new: 'info',
  contacted: 'warning',
  converted: 'success',
  archived: 'default',
};

const statusLabels = {
  new: 'New',
  contacted: 'Contacted',
  converted: 'Converted',
  archived: 'Archived',
};

const Contacts = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showSnackbar } = useApp();

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 0, limit: 25, total: 0 });
  const [apiKeys, setApiKeys] = useState([]);

  // Filters
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [apiKeyFilter, setApiKeyFilter] = useState(searchParams.get('apiKey') || '');

  // Menu
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);

  // Selected API key for dynamic columns
  const selectedApiKey = apiKeys.find(k => k._id === apiKeyFilter);

  const fetchApiKeys = useCallback(async () => {
    try {
      const response = await contactApi.getApiKeys();
      setApiKeys(response.data);
    } catch (error) {
      console.error('Failed to load API keys', error);
    }
  }, []);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page + 1,
        limit: pagination.limit,
        search: search || undefined,
        status: statusFilter || undefined,
        apiKey: apiKeyFilter || undefined,
      };

      const response = await contactApi.getAll(params);
      setContacts(response.data.contacts);
      setPagination(prev => ({ ...prev, total: response.data.pagination.total }));
    } catch (error) {
      showSnackbar('Failed to load contacts', 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.limit, search, statusFilter, apiKeyFilter]);

  useEffect(() => {
    fetchApiKeys();
  }, [fetchApiKeys]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  // Debounced search
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearch(value);
      setPagination(prev => ({ ...prev, page: 0 }));
    }, 300),
    []
  );

  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value);
  };

  const handlePageChange = (event, newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleRowsPerPageChange = (event) => {
    setPagination(prev => ({ ...prev, limit: parseInt(event.target.value, 10), page: 0 }));
  };

  const handleMenuOpen = (event, contact) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedContact(contact);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedContact(null);
  };

  const handleStatusChange = async (contactId, newStatus) => {
    try {
      await contactApi.update(contactId, { status: newStatus });
      showSnackbar('Contact status updated', 'success');
      fetchContacts();
    } catch (error) {
      showSnackbar('Failed to update status', 'error');
    }
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (!selectedContact) return;
    try {
      await contactApi.delete(selectedContact._id);
      showSnackbar('Contact deleted', 'success');
      fetchContacts();
    } catch (error) {
      showSnackbar('Failed to delete contact', 'error');
    }
    handleMenuClose();
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setApiKeyFilter('');
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  const hasFilters = search || statusFilter || apiKeyFilter;

  // Get column configuration based on selected API key or default
  const getColumns = () => {
    if (selectedApiKey?.fieldMappings?.length > 0) {
      return selectedApiKey.fieldMappings.map(mapping => ({
        key: mapping.externalField,
        label: mapping.displayName,
        type: mapping.fieldType,
      }));
    }
    // Default columns when no specific API key is selected
    return [
      { key: 'email', label: 'Email', type: 'email' },
      { key: 'name', label: 'Name', type: 'text' },
      { key: 'message', label: 'Message', type: 'textarea' },
    ];
  };

  const columns = getColumns();

  // Get value from contact data map
  const getContactValue = (contact, key) => {
    if (!contact.data) return '-';
    // Handle both Map and Object
    const value = contact.data instanceof Map ? contact.data.get(key) : contact.data[key];
    return value || '-';
  };

  // Truncate long text for table display
  const truncateText = (text, maxLength = 50) => {
    if (!text || text === '-') return '-';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Box>
      {/* Toolbar */}
      <Paper sx={{ p: 1.5, mb: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Search contacts..."
            size="small"
            defaultValue={search}
            onChange={handleSearchChange}
            sx={{ minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" sx={{ fontSize: 18 }} />
                </InputAdornment>
              ),
              sx: { fontSize: '0.8rem' },
            }}
          />

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Form / API Key</InputLabel>
            <Select
              value={apiKeyFilter}
              onChange={(e) => {
                setApiKeyFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 0 }));
              }}
              label="Form / API Key"
              sx={{ fontSize: '0.8rem' }}
            >
              <MenuItem value="" dense>All Forms</MenuItem>
              {apiKeys.map((key) => (
                <MenuItem key={key._id} value={key._id} dense>
                  {key.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel sx={{ fontSize: '0.8rem' }}>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 0 }));
              }}
              label="Status"
              sx={{ fontSize: '0.8rem' }}
            >
              <MenuItem value="" dense>All</MenuItem>
              <MenuItem value="new" dense>New</MenuItem>
              <MenuItem value="contacted" dense>Contacted</MenuItem>
              <MenuItem value="converted" dense>Converted</MenuItem>
              <MenuItem value="archived" dense>Archived</MenuItem>
            </Select>
          </FormControl>

          {hasFilters && (
            <Button
              size="small"
              startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
              onClick={clearFilters}
              sx={{ fontSize: '0.75rem' }}
            >
              Clear
            </Button>
          )}

          <Box sx={{ flexGrow: 1 }} />

          <Typography variant="body2" color="text.secondary">
            {pagination.total} contact{pagination.total !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Paper>

      {/* Table */}
      <Paper>
        {loading ? (
          <LoadingSpinner message="Loading contacts..." />
        ) : contacts.length === 0 ? (
          <EmptyState
            title="No contacts found"
            description={hasFilters ? "Try adjusting your filters" : "Contact form submissions will appear here"}
          />
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.slice(0, 4).map((col) => (
                      <TableCell key={col.key} sx={{ py: 1, fontSize: '0.8rem', fontWeight: 600 }}>
                        {col.label}
                      </TableCell>
                    ))}
                    <TableCell sx={{ py: 1, fontSize: '0.8rem', fontWeight: 600 }}>Source</TableCell>
                    <TableCell sx={{ py: 1, fontSize: '0.8rem', fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ py: 1, fontSize: '0.8rem', fontWeight: 600 }}>Submitted</TableCell>
                    <TableCell sx={{ py: 1, fontSize: '0.8rem', fontWeight: 600 }} align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contacts.map((contact) => (
                    <TableRow
                      key={contact._id}
                      hover
                      sx={{ cursor: 'pointer', '& td': { py: 0.75 } }}
                      onClick={() => navigate(`/contacts/${contact._id}`)}
                    >
                      {columns.slice(0, 4).map((col) => (
                        <TableCell key={col.key}>
                          <Typography variant="caption" sx={{ fontSize: '0.85rem' }}>
                            {truncateText(getContactValue(contact, col.key))}
                          </Typography>
                        </TableCell>
                      ))}
                      <TableCell>
                        <Typography variant="caption" sx={{ fontSize: '0.8rem' }} color="text.secondary">
                          {contact.apiKey?.name || 'Unknown'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={statusLabels[contact.status]}
                          color={statusColors[contact.status]}
                          size="small"
                          sx={{ fontSize: '0.7rem', height: 22 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontSize: '0.8rem' }}>
                          {formatRelativeDate(contact.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              sx={{ p: 0.5 }}
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/contacts/${contact._id}`);
                              }}
                            >
                              <ViewIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                          <IconButton
                            size="small"
                            sx={{ p: 0.5 }}
                            onClick={(e) => handleMenuOpen(e, contact)}
                          >
                            <MoreIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={pagination.total}
              page={pagination.page}
              onPageChange={handlePageChange}
              rowsPerPage={pagination.limit}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50, 100]}
              sx={{ '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { fontSize: '0.75rem' } }}
            />
          </>
        )}
      </Paper>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          navigate(`/contacts/${selectedContact?._id}`);
          handleMenuClose();
        }}>
          View Details
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => handleStatusChange(selectedContact?._id, 'contacted')}
          disabled={selectedContact?.status === 'contacted'}
        >
          Mark as Contacted
        </MenuItem>
        <MenuItem
          onClick={() => handleStatusChange(selectedContact?._id, 'converted')}
          disabled={selectedContact?.status === 'converted'}
        >
          Mark as Converted
        </MenuItem>
        <MenuItem
          onClick={() => handleStatusChange(selectedContact?._id, 'archived')}
          disabled={selectedContact?.status === 'archived'}
        >
          Archive
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Contacts;
